import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, throwError, ReplaySubject, of } from 'rxjs';
import { catchError, switchMap, tap, filter, take, map } from 'rxjs/operators';
import { ProfileService } from '../services/util/profile.service';
import { captureSentryMessage } from '../utils/sentry';
import { NavigationService } from '../services/navigation.service';
import { PassportAccessTokenResponse, PassportService } from '../services/passport.service';
import { ExternalApiService } from '../services/external-api.service';
import { FeatureService } from '../services/util/feature-flag.service';
import { TokenRefreshService } from '../services/token-refresh.service';
import { MobileEventsService } from '../services/util/mobile-events.service';

@Injectable()
export class ErrorHandlerInterceptor implements HttpInterceptor {
  private profileService = inject(ProfileService);
  private passportService = inject(PassportService);
  private navigationService = inject(NavigationService);
  private externalApiService = inject(ExternalApiService);

  private refreshTokenInProgress = false;
  private tokenRefreshed$ = new ReplaySubject<string>(1);
  private featureService = inject(FeatureService);
  private tokenRefreshService = inject(TokenRefreshService);
  private mobileEventsService = inject(MobileEventsService);

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError(initialError => {
        if (initialError instanceof HttpErrorResponse) {
          if (initialError.status === 401) {
            return this.handle401Error(req, next);
          } else if (initialError.status === 403) {
            if (this.mobileEventsService.isWebView) {
              this.mobileEventsService.tokenExpired();
              return throwError(() => initialError);
            }

            this.profileService.logout();
            this.navigationService.redirectToPage('/');
            return throwError(() => initialError);
          }
        }
        return throwError(() => initialError);
      })
    );
  }

  private handle401Error(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.refreshTokenInProgress) {
      this.refreshTokenInProgress = true;
      this.tokenRefreshed$ = new ReplaySubject<string>(1);
      this.tokenRefreshService.startRefresh();

      return this.passportService.updateUserToken().pipe(
        tap({
          next: ({ access_token }) => {
            this.refreshTokenInProgress = false;
            this.tokenRefreshService.stopRefresh();
            this.profileService.updateUserToken(access_token);
            this.tokenRefreshed$.next(access_token);
            this.tokenRefreshed$.complete();
          },
          error: error => {
            this.refreshTokenInProgress = false;
            this.tokenRefreshService.stopRefresh();
            this.tokenRefreshed$.error(error);
            captureSentryMessage('update-token-failed', error);
          },
        }),
        switchMap((passportAccessResponse: PassportAccessTokenResponse) => {
          if (this.featureService.isPassportFlowEnabled()) {
            return of(passportAccessResponse);
          }

          return this.externalApiService.synchronize().pipe(
            map(() => passportAccessResponse),
            tap(() => {
              this.mobileEventsService.tokenUpdated(passportAccessResponse.access_token);
            })
          );
        }),
        switchMap(({ access_token }) => this.fetchWithToken({ req, next, token: access_token }))
      );
    }

    return this.tokenRefreshed$.pipe(
      filter(token => token !== null),
      take(1),
      switchMap(token => this.fetchWithToken({ req, next, token }))
    );
  }

  private fetchWithToken({
    req,
    next,
    token,
  }: {
    req: HttpRequest<any>;
    next: HttpHandler;
    token: string;
  }): Observable<HttpEvent<any>> {
    const newReq = req.clone({
      withCredentials: true,
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + token,
      }),
    });
    return next.handle(newReq);
  }
}
